/**
 * `ErrorBoundary` — a small class-component error boundary that
 * degrades a transient render failure to an in-place fallback rather
 * than blanking the whole page.
 *
 * Background — the founder hit a "blank Guardrails" symptom when
 * switching the active site/domain: per-site React state (an open
 * mandate drawer, selected shadow row, etc.) referenced the OLD
 * site's data while the new `site_id` was already in props, which
 * threw during render. We've reset that state in lockstep with the
 * site switch (the primary fix), but a future transient mismatch
 * should NOT take the whole view down — this boundary catches the
 * residual case and surfaces a "couldn't render — retry" pill in
 * design-system chrome.
 *
 * Deliberately minimal:
 *
 *   - Class component with `getDerivedStateFromError` + a tiny
 *     `componentDidCatch` console.error. No 3p dependency, no
 *     telemetry plumbing.
 *   - `resetKey` is the reset signal — when its value changes the
 *     boundary clears its error state and re-renders children. The
 *     Guardrails view passes `activeSite.site_id` so a fresh site
 *     switch always gets a clean attempt without a manual refresh.
 *   - The default fallback is a tiny functional sub-component so it
 *     can read the design-system tokens via `useC()`; the class
 *     boundary itself cannot (no hooks in class components).
 *
 * Not a substitute for fixing the underlying state-reset bug — it's
 * the safety net under it. See the `dry-run-context.tsx` per-site
 * reset effect for the primary cleanup, and `Guardrails.tsx` for
 * the per-site UI-state reset.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional render-prop fallback. Receives the caught error +
   * a reset callback that clears the boundary's error state.
   */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
  /**
   * When this value changes between renders, the boundary clears
   * its error state. Use it to wire the "fresh attempt on site
   * switch" path — pass the active site_id and the next mount of
   * a fresh subtree gets a clean run automatically.
   */
  resetKey?: string | number | null;
  /**
   * `data-testid` for the default fallback wrapper. Defaults to
   * `error-boundary-fallback`.
   */
  testId?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  /** First component frame from React's componentStack, e.g. "SenderCard". */
  where: string | null;
}

/** "at SenderCard (…)" → "SenderCard"; null when the stack is empty. */
export function firstComponentFrame(stack: string | null | undefined): string | null {
  if (typeof stack !== 'string') return null;
  const m = /^\s*(?:at\s+)?([A-Za-z_$][\w$.]*)/m.exec(stack.trim());
  return m?.[1] ?? null;
}

function DefaultErrorFallback({
  reset,
  testId,
  error,
  where,
}: {
  reset: () => void;
  testId: string;
  error: Error;
  where: string | null;
}): JSX.Element {
  const C = useC();
  // Inline `<div>` rather than the `Card` atom — `Card` does not
  // forward arbitrary DOM props, so a `data-testid` on it would be
  // silently dropped. The visual chrome matches the surface-tier +
  // hairline-border idiom Card uses (same `C.surface` / `C.border`
  // tokens, same 4px radius).
  return (
    <div
      data-testid={testId}
      className="p-4 sm:p-6"
      style={{
        position: 'relative',
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        marginTop: 16,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          color: C.amberFg,
          marginBottom: 8,
        }}
      >
        COULDN’T RENDER
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          lineHeight: 1.5,
          color: C.text,
          marginBottom: 12,
          maxWidth: 520,
        }}
      >
        This view hit a transient error and stopped rendering. Click
        retry to try again, or switch site and back.
      </div>
      {/* 2026-11-01 — the console is out of reach on a phone; show the
          error itself so a report can carry it. No user data rides in
          a render error's message. */}
      <div
        data-testid={`${testId}-detail`}
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          lineHeight: 1.6,
          color: C.muted,
          marginBottom: 12,
          maxWidth: 520,
          overflowWrap: 'anywhere',
        }}
      >
        {`${error.name}: ${error.message}`.slice(0, 400)}
        {where !== null ? ` · in ${where}` : ''}
      </div>
      <button
        type="button"
        onClick={reset}
        data-testid={`${testId}-retry`}
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1,
          padding: '6px 10px',
          border: `1px solid ${C.amberFg}`,
          background: 'transparent',
          color: C.amberFg,
          borderRadius: 2,
          cursor: 'pointer',
        }}
      >
        RETRY
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, where: null };

  static getDerivedStateFromError(error: Error): Pick<ErrorBoundaryState, 'error'> {
    return { error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset on a fresh `resetKey` — mirrors how React 18 keys a
    // subtree but lets the boundary survive (so the reset doesn't
    // tear down the boundary itself).
    if (
      this.state.error !== null
      && prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ error: null, where: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Cheap console-only signal — we don't ship a telemetry bag
    // here. The visible fallback is the user-facing surface.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ where: firstComponentFrame(info.componentStack) });
  }

  reset = (): void => {
    this.setState({ error: null, where: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    if (this.props.fallback !== undefined) {
      return this.props.fallback({ error, reset: this.reset });
    }

    const testId = this.props.testId ?? 'error-boundary-fallback';
    return <DefaultErrorFallback reset={this.reset} testId={testId} error={error} where={this.state.where} />;
  }
}
